// Utility for cycling through spinner messages every 4 seconds
// Prevents repetition by tracking which messages have been shown

export class SpinnerCycler {
  constructor() {
    this.activeCyclers = new Map() // Map of spinner ID to { element, intervalId }
    this.messageSets = {
      thinking: [
        'Thinking...',
        'Analyzing your request...',
        'Processing your message...',
        'Working on it...',
        'Almost there...',
        'Gathering information...'
      ],
      get_synthetic_claim: [
        'Estimating the services you might need...',
        'Analyzing your medical needs...',
        'Determining required services...',
        'Calculating service estimates...',
        'Reviewing service requirements...',
        'Evaluating your care needs...'
      ],
      get_providers: [
        'Searching for providers...',
        'Finding the best matches...',
        'Scanning provider network...',
        'Locating nearby providers...',
        'Matching providers to your needs...',
        'Discovering available options...'
      ],
      get_spd: [
        'Checking your plan details...',
        'Reviewing your coverage...',
        'Analyzing plan information...',
        'Fetching plan details...',
        'Verifying your benefits...',
        'Examining plan specifics...'
      ],
      default: [
        'Processing your request...',
        'Working on your request...',
        'Handling your request...',
        'Processing...',
        'In progress...',
        'Please wait...'
      ]
    }
  }

  // Initialize cycling for a spinner element
  startCycling(element, spinnerType = 'default') {
    // Check if element is still connected
    if (!element.isConnected) {
      return
    }
    
    // Generate a unique ID for this spinner if it doesn't have one
    if (!element.dataset.spinnerId) {
      element.dataset.spinnerId = `spinner-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    }
    const spinnerId = element.dataset.spinnerId
    
    // Skip if already cycling (check by ID, not element reference)
    if (this.activeCyclers.has(spinnerId)) {
      const existing = this.activeCyclers.get(spinnerId)
      // If the existing element is disconnected but we have a new connected element with same ID, replace it
      if (!existing.element.isConnected && element.isConnected) {
        // Stop old interval
        clearInterval(existing.intervalId)
        // Will start new cycling below
      } else {
        return
      }
    }
      
    // If element has stale cycling attribute but isn't in our map, clear it
    if (element.dataset.spinnerCycling === 'true') {
      delete element.dataset.spinnerCycling
    }

    const messages = this.messageSets[spinnerType] || this.messageSets.default
    
    const textElement = element.querySelector('p.text-sm')
    
    if (!textElement) {
      console.error('[SpinnerCycler] No text element found! Element HTML:', element.outerHTML.substring(0, 200))
      return
    }

    // Mark as cycling
    element.dataset.spinnerCycling = 'true'
    element.dataset.spinnerType = spinnerType
    element.dataset.spinnerIndex = '0'

    // Shuffle messages to ensure variety (Fisher-Yates shuffle)
    const shuffledMessages = this.shuffleArray([...messages])
    element.dataset.spinnerMessages = JSON.stringify(shuffledMessages)
    
    // Start cycling every 2 seconds
    const intervalId = setInterval(() => {
      // Get current element from active cyclers (in case it was updated)
      const currentEntry = this.activeCyclers.get(spinnerId)
      if (!currentEntry) {
        clearInterval(intervalId)
        return
      }
      
      let currentElement = currentEntry.element
      
      // Check if element is still in the DOM
      if (!currentElement.isConnected) {
        clearInterval(intervalId)
        this.activeCyclers.delete(spinnerId)
        return
      }
      
      const currentIndex = parseInt(currentElement.dataset.spinnerIndex || '0', 10)
      const storedMessages = JSON.parse(currentElement.dataset.spinnerMessages || '[]')
      
      if (storedMessages.length === 0) {
        console.error('[SpinnerCycler] No stored messages, clearing interval')
        clearInterval(intervalId)
        this.activeCyclers.delete(spinnerId)
        return
      }

      // Get next message index (cycle through all messages)
      const nextIndex = (currentIndex + 1) % storedMessages.length
      currentElement.dataset.spinnerIndex = nextIndex.toString()
      
      // Update text
      const currentTextElement = currentElement.querySelector('p.text-sm')
      if (currentTextElement) {
        currentTextElement.textContent = storedMessages[nextIndex]
      } else {
        console.error('[SpinnerCycler] Text element not found during interval! Element may have been modified.')
      }
    }, 4000)

    // Store interval ID for cleanup (keyed by spinner ID, not element)
    this.activeCyclers.set(spinnerId, { element, intervalId })
  }

  // Stop cycling for a specific element
  stopCycling(element) {
    const spinnerId = element.dataset?.spinnerId
    
    if (!spinnerId) {
      // Fallback: try to find by element reference (for backwards compatibility)
      this.activeCyclers.forEach((value, id) => {
        if (value.element === element) {
          clearInterval(value.intervalId)
          this.activeCyclers.delete(id)
          if (element && element.isConnected && element.dataset) {
            delete element.dataset.spinnerCycling
          }
          return
        }
      })
      return
    }
    
    const entry = this.activeCyclers.get(spinnerId)
    if (entry) {
      clearInterval(entry.intervalId)
      this.activeCyclers.delete(spinnerId)
      // Clear the cycling attribute if element is still connected (might be disconnected)
      if (element && element.isConnected && element.dataset) {
        delete element.dataset.spinnerCycling
      }
    }
  }

  // Check if an element is already being cycled
  isCycling(element) {
    const spinnerId = element.dataset?.spinnerId
    if (!spinnerId) return false
    const entry = this.activeCyclers.get(spinnerId)
    return !!entry && entry.element.isConnected
  }

  // Clean up cyclers for elements that are no longer in the DOM
  cleanupDisconnectedElements() {
    const disconnectedIds = []
    
    this.activeCyclers.forEach((entry, spinnerId) => {
      if (!entry.element.isConnected) {
        disconnectedIds.push(spinnerId)
      }
    })
    
    disconnectedIds.forEach((spinnerId) => {
      const entry = this.activeCyclers.get(spinnerId)
      if (entry) {
        clearInterval(entry.intervalId)
        this.activeCyclers.delete(spinnerId)
      }
    })
  }

  // Stop all cycling
  stopAll() {
    this.activeCyclers.forEach((entry, spinnerId) => {
      clearInterval(entry.intervalId)
      if (entry.element && entry.element.dataset) {
        delete entry.element.dataset.spinnerCycling
      }
    })
    this.activeCyclers.clear()
  }

  // Fisher-Yates shuffle algorithm
  shuffleArray(array) {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Initialize all spinners in a container
  initializeSpinners(container = document) {
    // First, clean up any disconnected elements
    this.cleanupDisconnectedElements()
    
    // Find all spinner elements
    const spinnerElements = container.querySelectorAll('[data-spinner-type]')
    const activeSpinnerIds = new Set(this.activeCyclers.keys())
    
    spinnerElements.forEach((element) => {
      const spinnerType = element.dataset.spinnerType || 'default'
      
      // Generate ID if missing
      if (!element.dataset.spinnerId) {
        element.dataset.spinnerId = `spinner-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
      }
      const spinnerId = element.dataset.spinnerId
      
      const isIdActive = activeSpinnerIds.has(spinnerId)
      const existingEntry = this.activeCyclers.get(spinnerId)
      const existingElementConnected = existingEntry ? existingEntry.element.isConnected : false
      
      // Only start cycling if:
      // 1. Element is connected
      // 2. Not already cycling (by ID)
      // 3. If there's an existing entry but element is disconnected, we'll replace it in startCycling
      if (element.isConnected && !isIdActive) {
        this.startCycling(element, spinnerType)
      } else if (isIdActive && !existingElementConnected && element.isConnected) {
        // Special case: existing entry has disconnected element, but we have a new connected one
        // This handles Turbo Stream replacements - startCycling will handle the replacement
        this.startCycling(element, spinnerType)
      } else if (element.isConnected && isIdActive && existingElementConnected) {
        // Element is connected and already cycling - verify it's actually cycling
        const entry = this.activeCyclers.get(spinnerId)
        if (entry && entry.element !== element) {
          // Element mismatch - replace it
          this.startCycling(element, spinnerType)
        }
      }
    })
  }
}

// Create a singleton instance
export const spinnerCycler = new SpinnerCycler()

